<template>
  <div class="glass-card p-4">
    <div class="flex justify-between items-center mb-4">
      <h3 class="font-bold flex items-center gap-2">
        <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        Live Field Map
      </h3>
      <div class="flex gap-2">
        <button @click="toggleWeather" class="p-2 rounded-lg glass-card hover-lift" :class="{ 'bg-blue-100': weatherVisible }">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>
          </svg>
        </button>
        <button @click="toggleHeatmap" class="p-2 rounded-lg glass-card hover-lift" :class="{ 'bg-red-100': heatmapVisible }">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
          </svg>
        </button>
        <button @click="centerMap" class="p-2 rounded-lg glass-card hover-lift">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Mock Map Container -->
    <div class="relative bg-gradient-to-br from-emerald-50 via-white to-sky-50 dark:from-emerald-950 dark:via-gray-900 dark:to-sky-950 rounded-2xl overflow-hidden shadow-inner" style="height: 400px;">
      <!-- Grid Pattern -->
      <svg class="absolute inset-0 w-full h-full opacity-10">
        <defs>
          <pattern id="mock-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#mock-grid)" />
      </svg>

      <!-- Mock Map SVG -->
      <svg viewBox="0 0 400 320" class="w-full h-full relative z-10">
        <!-- Field polygons -->
        <defs>
          <linearGradient id="mock-field1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#10b981;stop-opacity:0.3" />
            <stop offset="100%" style="stop-color:#059669;stop-opacity:0.1" />
          </linearGradient>
          <linearGradient id="mock-field2" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:0.3" />
            <stop offset="100%" style="stop-color:#1d4ed8;stop-opacity:0.1" />
          </linearGradient>
          <linearGradient id="mock-field3" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:0.3" />
            <stop offset="100%" style="stop-color:#d97706;stop-opacity:0.1" />
          </linearGradient>
        </defs>

        <!-- Interactive fields -->
        <path d="M 40 40 L 180 40 L 200 120 L 160 140 L 40 120 Z" 
              fill="url(#mock-field1)" 
              stroke="#10b981" 
              stroke-width="2"
              class="hover:stroke-green-400 transition-all cursor-pointer hover:fill-opacity-60"
              @click="selectField(mockFields[0])">
          <animate attributeName="opacity" values="0.7;1;0.7" dur="4s" repeatCount="indefinite"/>
        </path>

        <path d="M 220 60 L 340 80 L 320 160 L 200 140 L 220 60 Z" 
              fill="url(#mock-field2)" 
              stroke="#3b82f6" 
              stroke-width="2"
              class="hover:stroke-blue-400 transition-all cursor-pointer hover:fill-opacity-60"
              @click="selectField(mockFields[1])">
        </path>

        <path d="M 60 180 L 200 180 L 220 260 L 80 280 L 60 180 Z" 
              fill="url(#mock-field3)" 
              stroke="#f59e0b" 
              stroke-width="2"
              stroke-dasharray="5,5"
              class="hover:stroke-yellow-400 transition-all cursor-pointer hover:fill-opacity-60"
              @click="selectField(mockFields[2])">
        </path>

        <!-- Animated drones -->
        <g v-for="drone in drones" :key="drone.id">
          <!-- Pulse ring -->
          <circle :cx="drone.x" :cy="drone.y" r="20" fill="none" :stroke="drone.color" stroke-width="1" opacity="0.3">
            <animate attributeName="r" values="10;25;10" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite"/>
          </circle>
          
          <!-- Drone icon -->
          <g :transform="`translate(${drone.x}, ${drone.y})`" class="cursor-pointer" @click="selectDrone(drone)">
            <circle r="8" :fill="drone.color"/>
            <text y="4" text-anchor="middle" class="text-xs font-bold fill-white">{{ drone.id }}</text>
          </g>
        </g>

        <!-- Flight paths -->
        <path d="M 110 80 Q 150 100 270 110" 
              stroke="#10b981" 
              stroke-width="2" 
              fill="none" 
              stroke-dasharray="4,4" 
              opacity="0.6">
          <animate attributeName="stroke-dashoffset" values="0;8" dur="1s" repeatCount="indefinite"/>
        </path>

        <!-- Weather overlay when enabled -->
        <g v-if="weatherVisible" class="pointer-events-none">
          <rect x="320" y="20" width="60" height="40" fill="white" stroke="#3b82f6" stroke-width="2" rx="8"/>
          <text x="350" y="35" text-anchor="middle" class="text-xs font-semibold">☀️</text>
          <text x="350" y="50" text-anchor="middle" class="text-xs">28°C</text>
        </g>

        <!-- Heatmap overlay when enabled -->
        <g v-if="heatmapVisible" class="pointer-events-none opacity-60">
          <circle cx="110" cy="80" r="30" fill="red" opacity="0.3"/>
          <circle cx="270" cy="110" r="25" fill="orange" opacity="0.3"/>
          <circle cx="150" cy="220" r="20" fill="yellow" opacity="0.3"/>
        </g>
      </svg>

      <!-- Stats Panel -->
      <div class="absolute top-4 left-4 glass-card p-3 rounded-xl">
        <h4 class="text-xs font-semibold mb-2 opacity-80">Live Statistics</h4>
        <div class="space-y-1 text-xs">
          <div class="flex justify-between gap-4">
            <span class="opacity-70">Active Fields</span>
            <span class="font-semibold">{{ mockFields.length }}</span>
          </div>
          <div class="flex justify-between gap-4">
            <span class="opacity-70">Drones Flying</span>
            <span class="font-semibold text-green-600">{{ activeDrones }}</span>
          </div>
          <div class="flex justify-between gap-4">
            <span class="opacity-70">Map Coverage</span>
            <span class="font-semibold">{{ mapCoverage }}%</span>
          </div>
        </div>
      </div>

      <!-- Weather Panel -->
      <div v-if="weatherVisible && mockWeather" class="absolute bottom-4 left-4 glass-card p-3 rounded-xl max-w-xs">
        <h4 class="text-xs font-semibold mb-2 flex items-center gap-2">
          <span class="text-lg">{{ mockWeather.icon }}</span>
          Weather Conditions
        </h4>
        <div class="space-y-1 text-xs">
          <div class="flex justify-between gap-4">
            <span class="opacity-70">Temperature</span>
            <span class="font-semibold">{{ mockWeather.temperature }}°C</span>
          </div>
          <div class="flex justify-between gap-4">
            <span class="opacity-70">Humidity</span>
            <span class="font-semibold">{{ mockWeather.humidity }}%</span>
          </div>
          <div class="flex justify-between gap-4">
            <span class="opacity-70">Wind</span>
            <span class="font-semibold">{{ mockWeather.windDirection }} {{ mockWeather.windSpeed }}</span>
          </div>
        </div>
        <div v-if="mockWeather.recommendations.length > 0" class="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p class="text-xs font-semibold mb-1">Drone Operations:</p>
          <ul class="text-xs themed-text-secondary space-y-1">
            <li v-for="rec in mockWeather.recommendations" :key="rec" class="flex items-start gap-1">
              <span class="text-green-500 mt-0.5">•</span>
              <span>{{ rec }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Selected Field Info -->
    <div v-if="selectedField" class="mt-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl">
      <div class="flex justify-between items-start mb-2">
        <h4 class="font-semibold">{{ selectedField.name }}</h4>
        <button @click="selectedField = null" class="text-gray-400 hover:text-gray-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <div class="grid grid-cols-3 gap-4 text-sm">
        <div>
          <span class="opacity-70">Area</span>
          <p class="font-semibold">{{ selectedField.area }} ha</p>
        </div>
        <div>
          <span class="opacity-70">Soil pH</span>
          <p class="font-semibold">{{ selectedField.soilPH }}</p>
        </div>
        <div>
          <span class="opacity-70">Moisture</span>
          <p class="font-semibold">{{ selectedField.moisture }}%</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const weatherVisible = ref(false)
const heatmapVisible = ref(false)
const selectedField = ref(null)

const mockFields = ref([
  { id: 'field1', name: 'North Field Alpha', area: 45, soilPH: '6.8', moisture: '68%', crop: 'Rice' },
  { id: 'field2', name: 'East Field Beta', area: 32, soilPH: '7.1', moisture: '72%', crop: 'Cassava' },
  { id: 'field3', name: 'South Field Gamma', area: 28, soilPH: '5.9', moisture: '45%', crop: 'Sugarcane' }
])

const drones = ref([
  { id: 'D1', x: 110, y: 80, color: '#10b981', status: 'active', battery: 87 },
  { id: 'D2', x: 270, y: 110, color: '#3b82f6', status: 'monitoring', battery: 92 },
  { id: 'D3', x: 150, y: 220, color: '#f59e0b', status: 'idle', battery: 45 }
])

const mockWeather = ref(null)

const activeDrones = computed(() => drones.value.filter(d => d.status === 'active').length)
const mapCoverage = computed(() => Math.round((mockFields.value.length * 35)))

let updateInterval = null

const selectField = (field) => {
  selectedField.value = field
}

const selectDrone = (drone) => {
  console.log('Selected drone:', drone)
  // Could show drone details modal
}

const toggleWeather = () => {
  weatherVisible.value = !weatherVisible.value
  if (weatherVisible.value && !mockWeather.value) {
    mockWeather.value = {
      icon: '☀️',
      temperature: 28,
      humidity: 65,
      windDirection: 'SW',
      windSpeed: '3',
      recommendations: ['สภาพอากาศเหมาะสำหรับการใช้โดรน', 'ทัศนวิสัยดี', 'ไม่มีฝนคัดงวด']
    }
  }
}

const toggleHeatmap = () => {
  heatmapVisible.value = !heatmapVisible.value
}

const centerMap = () => {
  console.log('Center map on user location')
}

onMounted(() => {
  // Simulate real-time updates
  updateInterval = setInterval(() => {
    drones.value = drones.value.map(drone => ({
      ...drone,
      x: drone.x + (Math.random() - 0.5) * 2,
      y: drone.y + (Math.random() - 0.5) * 2,
      battery: Math.max(0, drone.battery - Math.random() * 0.5)
    }))
  }, 3000)
})

onUnmounted(() => {
  if (updateInterval) {
    clearInterval(updateInterval)
  }
})
</script>
